/* =========================================================================
   Journey Director — "Today's Pilgrimage". A gentle guide, not a rail: it looks
   at the day's quest log, the daily blessing, feast visit, and inventory, and
   suggests the next good stop as a card with a CTA. Every suggestion is optional;
   nothing forces navigation, and when the day is done (or the player opted out)
   the director returns null and the home screen renders exactly as it does today.

   nextStop() is a PURE reducer (unit-tested); the render seams below read live
   state (quests/inventory/blessing/auth), draw the home + results cards, and
   route CTAs through the screen registry (feast CTA stays hidden until Wave 4's
   feast screen registers its opener).
   ========================================================================= */
import { esc } from './engine';
import { icon, isIconName } from './ui/icons';
import { track } from './telemetry';
import { isAuthenticated } from './api';
import { getLine } from './characters/registry';
import { getQuestLog, type QuestLogRow } from './quests';
import { getQty } from './inventory';
import { isLitToday } from './memorial';
import { hasOpener, openTarget, type CtaTarget } from './screens/screenRegistry';
import { isPilgrimageEnabled } from './featureFlags';
import { currentTier } from './progression';

export type StopId = 'feast' | 'scripture' | 'dyk' | 'arcade' | 'gtky' | 'relic';

export interface JourneyState {
  /** questKey → completed? snapshot of today's quest log. */
  quests: Record<string, boolean>;
  blessingLit: boolean;
  feastVisited: boolean;
  arcadeTickets: number;
  authed: boolean;
  optedOut: boolean;
  /** Stops whose CTA destination isn't available yet (feature-detected). */
  skip?: StopId[];
}

export interface JourneyStop {
  stopId: StopId;
  theoMoment: 'journey-next';
  cta: CtaTarget;
}

/** Fixed stop priority (architecture §1): feast → scripture → dyk (implicit via
    play) → arcade → gtky → relic. play-trivia is the default CTA for stops that
    need gameplay. */
const STOP_ORDER: ReadonlyArray<{ stopId: StopId; questKey: string; cta: CtaTarget }> = [
  // feast ("Meet Today's Saint"), gtky ("A Word with a Friend"), and relic
  // ("Relic Hunter") stops removed (production/TODO.md + db/039, 2026-08-04 —
  // the first two never had a working completion path; the third worked fine
  // but Eric found it confusing and chose to cut it). StopId/CtaTarget keep
  // the values so the skip/label logic below still typechecks; they're simply
  // never offered now.
  { stopId: 'scripture', questKey: 'daily_scripture20', cta: 'play' },
  { stopId: 'dyk', questKey: 'daily_dyk1', cta: 'play' },
  { stopId: 'arcade', questKey: 'daily_arcade1', cta: 'arcade' },
];

/** PURE: the next uncompleted stop in priority order, or null when the day's log
    is complete / the player opted out / every remaining stop is unavailable.

    `tier` (default 1 — full disclosure) gates which stops are offered: at Tier 0
    (new player) only play-flavored stops (cta 'play') are suggested; the deeper
    feast / arcade / gtky stops stay hidden until the player dives in. */
export function nextStop(state: JourneyState, tier: 0 | 1 = 1): JourneyStop | null {
  if (state.optedOut) return null;
  const skip = new Set(state.skip || []);
  for (const s of STOP_ORDER) {
    if (skip.has(s.stopId)) continue;
    if (tier === 0 && s.cta !== 'play') continue; // Tier 0: play-flavored suggestions only
    if (state.quests[s.questKey]) continue; // already complete
    return { stopId: s.stopId, theoMoment: 'journey-next', cta: s.cta };
  }
  return null;
}

/* ------------------------------ render seams ------------------------------ */

const OPTOUT_KEY = 'catholics.journey.optout';

function isOptedOut(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(OPTOUT_KEY) === '1';
  } catch {
    return false;
  }
}

/** Build the live JourneyState from client state (feature-detecting the feast
    screen: its CTA is skipped until Wave 4 registers an opener). */
function gatherState(log: QuestLogRow[]): JourneyState {
  const quests: Record<string, boolean> = {};
  for (const r of log) quests[r.key] = r.completed;
  return {
    quests,
    blessingLit: isLitToday(),
    feastVisited: !!quests['daily_feast'],
    arcadeTickets: getQty('arcade_ticket'),
    authed: isAuthenticated(),
    optedOut: isOptedOut(),
    skip: hasOpener('feast') ? [] : ['feast'],
  };
}

const CTA_LABEL: Record<CtaTarget, string> = {
  play: 'Play trivia',
  arcade: 'Open the Arcade',
  gtky: 'A word with Theo',
  feast: "Today's feast",
};

let lastSuggested: StopId | null = null;

function questRowsHTML(log: QuestLogRow[]): string {
  return log
    .map((r) => {
      const state = r.completed ? '✓' : `${r.progress}/${r.targetCount}`;
      return (
        `<div class="pilg-quest${r.completed ? ' done' : ''}">` +
        `<span class="pq-ic">${isIconName(r.icon) ? icon(r.icon, 16) : esc(r.icon)}</span>` + // server-supplied — escaped unless it names a brand icon
        `<span class="pq-title">${esc(r.title)}</span>` +
        `<span class="pq-prog">${state}</span>` +
        `</div>`
      );
    })
    .join('');
}

/** Render (or hide) the "Today's Pilgrimage" card on the home screen. Null stop
    → the card is hidden and home is exactly as today. */
export function renderJourneyHome(): void {
  const card = document.getElementById('pilgrimageCard');
  if (!card) return;
  // Pilgrimage paused (featureFlags.ts) — keep the Today's-Pilgrimage card dark.
  if (!isPilgrimageEnabled()) {
    card.classList.add('hidden');
    card.innerHTML = '';
    lastSuggested = null;
    return;
  }
  // Tier 0 (new player): the Today's Pilgrimage quest-log card is hidden entirely
  // — the home stays to one clear action until deep mode unlocks.
  if (currentTier() === 0) {
    card.classList.add('hidden');
    card.innerHTML = '';
    lastSuggested = null;
    return;
  }
  const log = getQuestLog();
  const stop = nextStop(gatherState(log));
  if (!stop) {
    card.classList.add('hidden');
    card.innerHTML = '';
    lastSuggested = null;
    return;
  }
  card.classList.remove('hidden');
  card.innerHTML =
    `<div class="pilg-head">${icon('compass', 16)} Today's Pilgrimage</div>` +
    `<div class="pilg-quests">${questRowsHTML(log)}</div>` +
    `<div class="pilg-next">` +
    `<div class="pilg-theo">${esc(getLine('theo', stop.theoMoment))}</div>` +
    `<button class="btn pilg-cta" id="pilgCtaBtn" type="button" data-cta="${stop.cta}" data-stop="${stop.stopId}">${CTA_LABEL[stop.cta]}</button>` +
    `</div>`;
  wireCta('pilgCtaBtn');
  suggested(stop.stopId);
}

// Results already has its own "Play Again" button right below this card, and a
// finished game never leaves a snapshot to resume — so a 'play' cta here (2026-08-04,
// Eric: "Play Trivia" and "Play Again" stacked, doing the same thing, felt like
// clutter) would just start the exact same fresh Round 1 that "Play Again" does.
// Skip play-flavored stops on THIS screen only, so the card only ever surfaces
// something Play Again doesn't already cover (arcade, gtky, feast).
const PLAY_STOP_IDS: StopId[] = STOP_ORDER.filter((s) => s.cta === 'play').map((s) => s.stopId);

/** Render one journey card on the results screen, after the completion block. */
export function renderJourneyResults(): void {
  const host = document.getElementById('resJourney');
  if (!host) return;
  // Pilgrimage paused (featureFlags.ts) — no journey card on the results screen.
  if (!isPilgrimageEnabled()) {
    host.classList.add('hidden');
    host.innerHTML = '';
    return;
  }
  const log = getQuestLog();
  const state = gatherState(log);
  state.skip = [...(state.skip || []), ...PLAY_STOP_IDS];
  // First-unlock game only: the "Arcade unlocked!" strip above already carries
  // an arcade CTA — two arcade buttons on one screen (Eric 2026-08-16).
  const arcadeStrip = document.getElementById('resArcadeCta');
  if (arcadeStrip && !arcadeStrip.classList.contains('hidden')) state.skip = [...state.skip, 'arcade'];
  const stop = nextStop(state, currentTier());
  if (!stop) {
    host.classList.add('hidden');
    host.innerHTML = '';
    return;
  }
  host.classList.remove('hidden');
  host.innerHTML =
    `<div class="pilg-theo">${esc(getLine('theo', stop.theoMoment))}</div>` +
    `<button class="btn secondary pilg-cta" id="resJourneyBtn" type="button" data-cta="${stop.cta}" data-stop="${stop.stopId}">${CTA_LABEL[stop.cta]}</button>`;
  wireCta('resJourneyBtn');
  suggested(stop.stopId);
}

function suggested(stopId: StopId): void {
  if (stopId !== lastSuggested) {
    lastSuggested = stopId;
    track('JourneySuggested', { stopId });
  }
}

function wireCta(btnId: string): void {
  const btn = document.getElementById(btnId) as HTMLButtonElement | null;
  if (!btn) return;
  btn.onclick = () => {
    const cta = btn.dataset.cta as CtaTarget | undefined;
    const stopId = btn.dataset.stop;
    if (stopId) track('JourneyFollowed', { stopId });
    if (cta) openTarget(cta);
  };
}
